import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Grid,
  Button,
  Container,
  Alert
} from '@mui/material';
import {
  Security as SecurityIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ProtecaoAcesso from '../components/ProtecaoAcesso';
import { PrimaryButton } from '../components';

interface ConfiguracaoItem {
  titulo: string;
  descricao: string;
  icone: React.ReactNode;
  rota: string;
  somenteDivulgacao?: boolean;
}

const Configuracoes: React.FC = () => {
  const navigate = useNavigate();

  const configuracoes: ConfiguracaoItem[] = [
    {
      titulo: 'Perfis de Acesso',
      descricao: 'Gerencie perfis de usuários e acessos no sistema',
      icone: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      rota: '/tw/perfis'
    },
    {
      titulo: 'Colaboradores',
      descricao: 'Cadastre e gerencie colaboradores da empresa',
      icone: <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      rota: '/tw/colaboradores'
    }
  ];

  const handleNavigate = (rota: string) => {
    navigate(rota);
  };

  return (
    <ProtecaoAcesso
      tela="configuracoes-usuario"
      tipo="visualizacao"
    >
      <Container maxWidth="lg">
        <Box sx={{ py: 3 }}>
          {/* Cabeçalho */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <AdminIcon sx={{ fontSize: 48, color: 'primary.main' }} />
            </Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Configurações do Sistema
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Gerencie configurações administrativas e permissões de acesso
            </Typography>
          </Box>

          {/* Cards de Configurações */}
          <Grid container spacing={3}>
            {configuracoes.map((config) => (
              <Grid size={{ xs: 12, sm: 6, md: 6 }} key={config.rota}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, elevation 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      elevation: 8
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 2, pt: 4 }}>
                    <Box sx={{ mb: 2 }}>
                      {config.icone}
                    </Box>
                    <Typography
                      variant="h6"
                      component="h2"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      {config.titulo}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.6 }}
                    >
                      {config.descricao}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'center', p: 1, pb: 4 }}>
                    <PrimaryButton
                      variant="contained"
                      color="primary"
                      onClick={() => handleNavigate(config.rota)}
                      sx={{
                        minWidth: 120,
                        fontWeight: 500,
                        textTransform: 'none'
                      }}
                    >
                      Acessar
                    </PrimaryButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Informação adicional */}
          <Box sx={{ mt: 4 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Nota:</strong> Estas configurações são acessíveis apenas para administradores do sistema.
                Alterações feitas aqui afetam todos os usuários da plataforma.
              </Typography>
            </Alert>
          </Box>
        </Box>
      </Container>
    </ProtecaoAcesso>
  );
};

export default Configuracoes;